import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Users, CalendarCheck, Shield, History, CheckCircle2 } from 'lucide-react';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { supabase } from '../supabase';
type BookingType = 'individu' | 'kelompok' | null;
export function Konseling() {
  const [formData, setFormData] = useState({
    nama: '',
    kelas: '',
    no_hp: '',
    keluhan: '',
    jadwal: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingType, setBookingType] = useState<BookingType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    const {
      error
    } = await supabase.from('konseling_bookings').insert({
      user_id: user?.id,
      nama: formData.nama,
      kelas: formData.kelas,
      no_hp: formData.no_hp,
      keluhan: formData.keluhan,
      jadwal: formData.jadwal,
      jenis_konseling: bookingType
    });
    setIsSubmitting(false);
    if (error) {
      alert(error.message);
      return;
    }
    setFormData({
      nama: '',
      kelas: '',
      no_hp: '',
      keluhan: '',
      jadwal: ''
    });
    setBookingType(null);
    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
    }, 5000);
  };
  const renderBookingForm = () => {
    if (!bookingType) return null;
    const title = bookingType === 'individu' ? 'Jadwalkan Konseling Individu' : 'Jadwalkan Konseling Kelompok';
    return <Modal isOpen={!!bookingType} onClose={() => setBookingType(null)} title={title} data-id="element-331">
        <form onSubmit={handleBookingSubmit} className="space-y-4" data-id="element-332">
          <Input label="Nama Lengkap" value={formData.nama} onChange={e => setFormData({
          ...formData,
          nama: e.target.value
        })} data-id="element-333" />
          <Input label="Kelas" value={formData.kelas} onChange={e => setFormData({
          ...formData,
          kelas: e.target.value
        })} data-id="element-334" />
          <Input label="No. HP / WhatsApp" value={formData.no_hp} onChange={e => setFormData({
          ...formData,
          no_hp: e.target.value
        })} data-id="element-335" />
          <Input label="Keluhan / Masalah yang dihadapi" as="textarea" value={formData.keluhan} onChange={e => setFormData({
          ...formData,
          keluhan: e.target.value
        })} data-id="element-336" />
          <Input label="Jadwal yang dipilih" type="datetime-local" value={formData.jadwal} onChange={e => setFormData({
          ...formData,
          jadwal: e.target.value
        })} data-id="element-337" />

          <div className="bg-pastel-blue-light/50 p-4 rounded-xl flex items-start gap-3 mt-2" data-id="element-338">
            <Shield className="text-blue-600 w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-339" />
            <p className="text-xs text-blue-800" data-id="element-340">
              <strong data-id="element-341">Asas Kerahasiaan:</strong> Segala informasi yang kamu
              sampaikan dalam sesi konseling akan dijaga kerahasiaannya oleh
              Guru BK sesuai dengan kode etik profesi.
            </p>
          </div>

          <div className="pt-4" data-id="element-342">
            <Button type="submit" fullWidth isLoading={isSubmitting} data-id="element-343">
              Ajukan Jadwal Konseling
            </Button>
          </div>
        </form>
      </Modal>;
  };
  return <div className="min-h-screen bg-white py-12" data-id="element-344">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-345">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-346">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-347">
            <h1 className="text-4xl font-bold text-dark mb-6" data-id="element-348">
              Layanan Konseling
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-349">
              Konseling adalah proses pemberian bantuan yang dilakukan melalui
              wawancara oleh seorang ahli (konselor) kepada individu yang sedang
              mengalami sesuatu masalah (klien) yang bermuara pada teratasinya
              masalah yang dihadapi klien.
            </p>
            <div className="inline-flex items-center gap-2 bg-pastel-pink-light text-pink-800 px-4 py-2 rounded-full text-sm font-medium" data-id="element-350">
              <Shield size={16} data-id="element-351" />
              Kerahasiaan Terjamin 100%
            </div>

            <div className="mt-8" data-id="element-352">
              <Link to="/konseling-booking" data-id="element-353">
                <Button variant="outline" size="lg" data-id="element-354">
                  <History size={20} className="mr-2" data-id="element-355" />
                  Riwayat Konseling
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-356">
          {/* Konseling Individu */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-3xl p-8 border border-pastel-grey shadow-sm hover:shadow-apple transition-all duration-300 flex flex-col" data-id="element-357">
            <div className="w-16 h-16 rounded-2xl bg-pastel-blue-light flex items-center justify-center mb-6" data-id="element-358">
              <User className="text-blue-600 w-8 h-8" data-id="element-359" />
            </div>
            <h2 className="text-2xl font-bold text-dark mb-4" data-id="element-360">
              Konseling Individu
            </h2>
            <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-361">
              Layanan konseling yang diselenggarakan oleh Guru BK terhadap
              seorang siswa dalam rangka pengentasan masalah pribadi yang
              dialaminya. Sesi ini bersifat sangat privat dan rahasia.
            </p>
            <Button size="lg" fullWidth onClick={() => setBookingType('individu')} data-id="element-362">
              <CalendarCheck size={20} className="mr-2" data-id="element-363" /> Jadwalkan Sesi
            </Button>
          </motion.div>

          {/* Konseling Kelompok */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-3xl p-8 border border-pastel-grey shadow-sm hover:shadow-apple transition-all duration-300 flex flex-col" data-id="element-364">
            <div className="w-16 h-16 rounded-2xl bg-pastel-pink-light flex items-center justify-center mb-6" data-id="element-365">
              <Users className="text-pink-600 w-8 h-8" data-id="element-366" />
            </div>
            <h2 className="text-2xl font-bold text-dark mb-4" data-id="element-367">
              Konseling Kelompok
            </h2>
            <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-368">
              Layanan konseling yang diselenggarakan oleh Guru BK terhadap
              sekelompok siswa yang memiliki masalah serupa. Memanfaatkan
              dinamika kelompok untuk saling membantu memecahkan masalah.
            </p>
            <Button size="lg" variant="outline" fullWidth onClick={() => setBookingType('kelompok')} data-id="element-369">
              <CalendarCheck size={20} className="mr-2" data-id="element-370" /> Jadwalkan Sesi
            </Button>
          </motion.div>
        </div>

        {renderBookingForm()}

        {/* Success Modal */}
        <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="" data-id="element-371">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="text-center py-6" data-id="element-372">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.15,
            type: 'spring',
            stiffness: 300
          }} className="mx-auto mb-5" data-id="element-373">
              <div className="w-24 h-24 rounded-full bg-pink-100 flex items-center justify-center mx-auto" data-id="element-374">
                <CheckCircle2 className="w-14 h-14 text-pink-600" data-id="element-375" />
              </div>
            </motion.div>

            <h2 className="text-2xl font-bold text-gray-800 mb-3" data-id="element-376">
              Booking Berhasil 🎉
            </h2>

            <p className="text-gray-500 leading-relaxed max-w-sm mx-auto" data-id="element-377">
              Pengajuan jadwal konseling berhasil dikirim. Guru BK akan segera
              meninjau dan menghubungi Anda sesuai jadwal yang dipilih.
            </p>

            <div className="flex gap-3 mt-8" data-id="element-378">
              <Button variant="outline" fullWidth onClick={() => setShowSuccessModal(false)} data-id="element-379">
                Tutup
              </Button>

              <Link to="/konseling-booking" className="flex-1" data-id="element-380">
                <Button fullWidth data-id="element-381">Lihat Riwayat</Button>
              </Link>
            </div>
          </motion.div>
        </Modal>
      </div>
    </div>;
}